import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import * as _ from 'lodash';
import { takeUntil } from 'rxjs';
import { BaseComponent } from './../../../../core/components/base/base.component';
import { FeedbackService } from './../../../../core/services/feedback.service';
import { HospitalService } from './../../../../core/services/hospital.service';
import { PatientService } from './../../../../core/services/patient.service';

@Component({
  selector: 'app-inpatient-list',
  templateUrl: './inpatient-list.component.html',
  styleUrls: ['./inpatient-list.component.scss']
})
export class InpatientListComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy {
  @Input() filterValue: string = '';
  @Input() sortBy: 'surname' | 'firstName' | 'patientNumber' = 'surname';
  @Input() sortDirection: 'asc' | 'desc' = 'asc';
  inpatients: any[] = [];
  allHospitals: any[] = [];
  inpatientHospitals: any[] = [];
  filteredInpatients: any[] = [];
  filteredInpatientsByHospital: any = {};

  public expandedHospitalId: string | null = null;

  constructor(
    private patientService: PatientService,
    private hospitalService: HospitalService,
    fs: FeedbackService,
    md: MatDialog
  ) {
    super(fs, md);
  }

  ngOnInit() {
    this.patientService.inpatients$
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(inpatients => {
        this.inpatients = inpatients;
        this.filteredInpatients = inpatients; // Ensure filteredInpatients is initialized
        this.applyFilter(); // Apply filter initially
      });

    this.hospitalService.hospitals$
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(hospitals => {
        this.allHospitals = hospitals;
        this.updateInpatientHospitals();
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['filterValue'] || changes['sortBy'] || changes['sortDirection']) {
      this.applyFilter();
    }
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();

  }

  applyFilter() {
    if (!this.inpatients) {
      return;
    }

    if (this.filterValue.trim() === '') {
      this.filteredInpatients = this.inpatients;
    } else {
      const filterTerms = this.filterValue.toLowerCase().split(' ');
      this.filteredInpatients = _.filter(this.inpatients, patient => {

        const icdCodesMatch = patient.icdCodes?.some((icdCode: any) =>
          filterTerms.every(term =>
            _.includes((icdCode.code || '').toLowerCase(), term) ||
            _.includes((icdCode.description || '').toLowerCase(), term)
          )
        );

        const patientMatch = filterTerms.every(term =>
          _.includes(patient.name.toLowerCase(), term) ||
          _.includes(patient.surname.toLowerCase(), term) ||
          _.includes(patient.patientNumber?.toLowerCase(), term) ||
          _.includes(patient.diagnosis?.toLowerCase(), term)
        );

        return icdCodesMatch || patientMatch;
      }

      );
    }

    this.groupFilteredInpatientsByHospital();
  }

  groupFilteredInpatientsByHospital() {
    const prop = this.sortBy === 'firstName' ? 'name' : this.sortBy;
    const secondaryProp = prop === 'surname' ? 'name' : 'surname';
    const dir = this.sortDirection;
    const norm = (v: any) => (v == null ? '' : String(v)).trim().toLowerCase();
    const groupedByHospital = _.groupBy(this.filteredInpatients, 'hospitalId');
    this.filteredInpatientsByHospital = _.mapValues(groupedByHospital, patients => {
      // build an array of ward objects for this hospital
      const byWard = _.groupBy(patients, 'wardId');
      const wards = _.map(byWard, (patientsInWard, wardId) => ({
        wardId,
        name: patientsInWard[0].wardName,
        patients: _.orderBy(
          patientsInWard,
          [(p: any) => norm(p[prop]), (p: any) => norm(p[secondaryProp])],
          [dir, dir]
        )
      }));
      // sort wards by ward.name
      return _.orderBy(wards, ['name'], ['asc']);
    });

    this.updateInpatientHospitals();
  }

  updateInpatientHospitals() {
    if (!this.allHospitals) {
      return;
    }

    this.inpatientHospitals = _.orderBy(
      // filter hospitals that have any inpatients
      _.filter(this.allHospitals, hospital =>
        _.some(this.filteredInpatients, p => p.hospitalId === hospital.id)
      ),
      ['name'], ['asc']  // sort hospitals by name
    ).map(hospital => ({
      ...hospital,
      // inject the sorted wards array (or empty array)
      wards: this.filteredInpatientsByHospital[hospital.id] || []
    }));

    // auto-expand the first hospital if none selected yet
    if (this.inpatientHospitals.length > 0 && this.expandedHospitalId === null) {
      this.expandedHospitalId = this.inpatientHospitals[0].id;
    }
  }

  toggleHospitalList(hospitalId: string) {
    this.expandedHospitalId = this.expandedHospitalId === hospitalId ? null : hospitalId;
  }

}
